/* sd-chat.js - Rig Guide launcher and panel (protocol v2 client). No dependencies. Requests: the chat host, same-origin
	 /cart.js and /cart/add.js, Shopify CDN images. Never opens by itself. Server text is never markup: text nodes plus
	 markdown links to this store; cards, checklist and actions are rebuilt from validated fields (PURE block). */

(function () {
	'use strict';

	/* PURE:BEGIN - helpers with no DOM and no state; the unit tests run this block as is. */

	var HANDLE_RE = /^[a-z0-9][a-z0-9_-]{0,150}$/;
	var ROLES = { required: 'Needed', recommended: 'Recommended', optional: 'Optional', alternative: 'Alternative' };
	var STATES = { have: 1, in_cart: 1, missing: 1, optional: 1 };

	// Control characters become spaces; bidi overrides are removed.
	var BIDI = /[\u202a-\u202e\u2066-\u2069]/g;
	function str(v, max) {
		return typeof v === 'string' ? v.replace(BIDI, '').replace(/[\u0000-\u001f\u007f\u2028\u2029]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max) : '';
	}

	// $1,234.50 / $527; anything that is not a sane price is ''.
	function formatPrice(n) {
		if (typeof n !== 'number' || !isFinite(n) || n < 0 || n > 1000000) return '';
		var s = n.toFixed(2).split('.');
		var whole = s[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		return '$' + whole + (s[1] === '00' ? '' : '.' + s[1]);
	}

	function priceLabel(card) {
		if (card.price == null) return '';
		var a = formatPrice(card.price);
		if (!a) return '';
		var b = card.priceMax != null && card.priceMax > card.price ? formatPrice(card.priceMax) : '';
		return b ? a + ' - ' + b : a;
	}
	function productPath(url) {
		var u;
		if (typeof url !== 'string' || !url) return '';
		try { u = new URL(url, window.location.href); } catch (e) { return ''; }
		if (!storeHost(u.href) || u.username || u.password) return '';
		var m = /^\/products\/([a-z0-9][a-z0-9_-]{0,150})\/?$/.exec(u.pathname);
		return m ? '/products/' + m[1] : '';
	}

	function storePath(url) {
		var u;
		if (typeof url !== 'string' || !url) return '';
		try { u = new URL(url, window.location.href); } catch (e) { return ''; }
		if (!storeHost(u.href) || u.username || u.password) return '';
		return /^\/(pages|collections|products|blogs|cart|policies)(\/[A-Za-z0-9._~-]{1,160}){0,3}\/?$/.test(u.pathname) ? u.pathname : '';
	}

	// Shopify CDN images only (https), with a width cap.
	function imageSrc(url, width) {
		var u;
		if (typeof url !== 'string' || url.length > 600) return '';
		try { u = new URL(url); } catch (e) { return ''; }
		if (u.protocol !== 'https:' || u.username || u.password) return '';
		var host = u.hostname.toLowerCase();
		var ok = host === 'cdn.shopify.com' || (storeHost(u.href) && /^\/cdn\//.test(u.pathname));
		if (!ok) return '';
		u.searchParams.set('width', String(width || 200));
		return u.href;
	}

	function wholeNumber(v) {
		if (typeof v === 'string' && /^\d{1,16}$/.test(v)) v = Number(v);
		return typeof v === 'number' && isFinite(v) && v > 0 && Math.floor(v) === v && v <= 9007199254740991 ? v : 0;
	}

	function money(v) {
		return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 1000000 ? v : null;
	}
	function normalizeCard(raw) {
		if (!raw || typeof raw !== 'object') return null;
		var handle = typeof raw.handle === 'string' && HANDLE_RE.test(raw.handle) ? raw.handle : '';
		var path;
		if (raw.url != null && raw.url !== '') {
			path = productPath(raw.url);
			if (!path) return null;                    // a link we do not trust: the whole card goes
			handle = path.slice(10);
		} else if (handle) {
			path = '/products/' + handle;
		}
		if (!path || !handle) return null;
		var title = str(raw.title, 140);
		if (!title) return null;
		var note = str(raw.note, 100);
		var verified = raw.price_verified !== false;
		var price = verified ? money(raw.price) : null;
		var priceMax = verified && price != null ? money(raw.price_max) : null;
		var variantId = wholeNumber(raw.variant_id);
		var optionsRequired = raw.options_required === true;
		var available = raw.available === true ? true : raw.available === false ? false : null;
		return {
			handle: handle,
			href: path,
			title: title,
			brand: str(raw.brand, 40),
			image: imageSrc(raw.image, 200),
			price: price,
			priceMax: priceMax,
			available: available,
			variantId: variantId,
			optionsRequired: optionsRequired,
			role: Object.prototype.hasOwnProperty.call(ROLES, raw.role) ? raw.role : '',
			note: note,
			// Never cut a lead time: the server sends up to 60 characters and the card wraps.
			leadTime: str(raw.lead_time, 80),
			slot: typeof raw.slot === 'string' && /^[a-z0-9_-]{1,32}$/.test(raw.slot) ? raw.slot : '',
			fit: raw.fit === 'confirmed' || raw.fit === 'unconfirmed' ? raw.fit : /not confirmed/i.test(note) ? 'unconfirmed' : /^confirmed fit/i.test(note) ? 'confirmed' : '',
			inCart: raw.in_cart === true,
			canAdd: variantId > 0 && !optionsRequired && available !== false
		};
	}

	// The "Add the N you need" bar takes required or recommended parts with a confirmed or unflagged fit; never
	// alternatives (rivals for one slot), optional parts or unconfirmed fits.
	function isNeeded(c) {
		return !!c && c.canAdd && (c.role === 'required' || c.role === 'recommended') && c.fit !== 'unconfirmed';
	}
	function neededCards(cards) {
		var out = [], slots = {};
		for (var i = 0; i < cards.length; i++) {
			var c = cards[i];
			if (!isNeeded(c) || (c.slot && slots[c.slot])) continue;
			if (c.slot) slots[c.slot] = 1;
			out.push(c);
		}
		return out;
	}

	function normalizeCards(arr) {
		var out = [], seen = {};
		if (!Array.isArray(arr)) return out;
		for (var i = 0; i < arr.length && out.length < 4; i++) {
			var c = normalizeCard(arr[i]);
			if (c && !seen[c.handle]) { seen[c.handle] = 1; out.push(c); }
		}
		return out;
	}

	function normalizeChecklist(arr) {
		var out = [];
		if (!Array.isArray(arr)) return out;
		for (var i = 0; i < arr.length && out.length < 14; i++) {
			var r = arr[i];
			if (!r || typeof r !== 'object') continue;
			var label = str(r.label, 40);
			var slot = typeof r.slot === 'string' && /^[a-z0-9_-]{1,32}$/.test(r.slot) ? r.slot : '';
			if (!label || !slot) continue;
			out.push({ slot: slot, label: label, state: STATES[r.state] === 1 ? r.state : 'optional', note: str(r.note, 80) });
		}
		return out;
	}

	function normalizeSuggestions(arr) {
		var out = [];
		if (!Array.isArray(arr)) return out;
		for (var i = 0; i < arr.length && out.length < 3; i++) {
			var s = str(arr[i], 60);
			if (s) out.push(s);
		}
		return out;
	}

	// "+19526495710" or ''. Anything but digits and phone punctuation is rejected, not squeezed.
	function telDigits(v) {
		if (typeof v !== 'string' || !/^\+?[\d\s().-]{7,24}$/.test(v)) return '';
		var d = v.replace(/[^\d+]/g, '');
		if (/^\d{10}$/.test(d)) d = '+1' + d;
		else if (/^1\d{10}$/.test(d)) d = '+' + d;
		return /^\+\d{10,15}$/.test(d) ? d : '';
	}

	var EMAIL_RE = /^[A-Za-z0-9._+-]{1,64}@[A-Za-z0-9.-]{1,120}\.[A-Za-z]{2,12}$/;

	// ctx = the page's own tel / email, used when the server's are missing or wrong.
	function normalizeAction(raw, ctx) {
		if (!raw || typeof raw !== 'object') return null;
		ctx = ctx || {};
		if (raw.type === 'call') {
			var tel = telDigits(raw.tel) || ctx.tel || '';
			if (!tel) return null;
			return { type: 'call', label: str(raw.label, 60) || 'Call SimDrive Support', href: 'tel:' + tel,
				hours: str(raw.hours, 60), openNow: raw.open_now === true ? true : raw.open_now === false ? false : null, reason: str(raw.reason, 80) };
		}
		if (raw.type === 'email') {
			var m = /^mailto:([^?#\s]+)/i.exec(typeof raw.href === 'string' ? raw.href : '');
			var addr = m ? m[1] : (ctx.email || '');
			if (!EMAIL_RE.test(addr)) return null;
			var okHost = /@simdriveperformance\.com$/i.test(addr) || (ctx.email && addr.toLowerCase() === String(ctx.email).toLowerCase());
			if (!okHost) addr = ctx.email && EMAIL_RE.test(ctx.email) ? ctx.email : '';
			if (!addr) return null;
			return { type: 'email', label: str(raw.label, 60) || 'Email support', href: 'mailto:' + addr, hours: '', openNow: null, reason: '' };
		}
		if (raw.type === 'quote') {
			var p = storePath(raw.url);
			if (!p) return null;
			return { type: 'quote', label: str(raw.label, 60) || 'Request a written quote', href: p, hours: '', openNow: null, reason: '' };
		}
		return null;
	}

	function normalizeActions(arr, ctx) {
		var out = [];
		if (!Array.isArray(arr)) return out;
		for (var i = 0; i < arr.length && out.length < 3; i++) {
			var a = normalizeAction(arr[i], ctx);
			if (a) out.push(a);
		}
		return out;
	}
	function parseCart(data) {
		var out = { items: [], count: 0 };
		if (!data || typeof data !== 'object' || !Array.isArray(data.items)) return out;
		for (var i = 0; i < data.items.length && i < 100; i++) {
			var it = data.items[i];
			if (!it || typeof it !== 'object') continue;
			var handle = typeof it.handle === 'string' && HANDLE_RE.test(it.handle) ? it.handle : '';
			var qty = Math.floor(Number(it.quantity));
			if (!handle || !isFinite(qty) || qty < 1) continue;
			out.items.push({
				handle: handle,
				variantId: wholeNumber(it.variant_id || it.id),
				qty: Math.min(qty, 99),
				title: str(it.product_title || it.title, 120),
				productType: str(it.product_type, 60)
			});
			out.count += Math.min(qty, 99);
		}
		return out;
	}
	function cartPayload(cart) {
		if (!cart || !cart.items || !cart.items.length) return null;
		var items = [];
		for (var i = 0; i < cart.items.length && items.length < 40; i++) {
			var it = cart.items[i];
			var o = { handle: it.handle, qty: Math.max(1, Math.min(99, it.qty | 0)) };
			if (it.variantId) o.variant_id = it.variantId;
			items.push(o);
		}
		return { items: items, item_count: cart.count | 0 };
	}
	function parseAdded(data) {
		if (!data || typeof data !== 'object') return [];
		var list = Array.isArray(data.items) ? data.items : (data.handle ? [data] : []);
		return parseCart({ items: list }).items;
	}

	// Rigs, bases, cockpits, wheelbases, seats: worth a "what else does it need" nudge.
	function isFoundation(item) {
		if (!item) return false;
		var type = String(item.productType || '');
		var title = String(item.title || '');
		if (/mount|bracket|cushion|cover|adapter|adaptor|slider|upgrade|kit|pad\b|plate|spare|shaker|cable|pedal|shifter|monitor|stand\b|keyboard/i.test(title + ' ' + type)) return false;
		if (/motion|platform|cockpit|simulator|\brig\b|wheel ?base|direct drive|racing seat|\bseat\b|bundle/i.test(type)) return true;
		return /motion platform|motion simulator|cockpit|simulator|wheel ?base|direct drive|racing seat|\bseat\b|\brig\b/i.test(title);
	}
	function parseSSE(buf) {
		var tail = '';
		buf = String(buf);
		if (buf.charAt(buf.length - 1) === '\r') { tail = '\r'; buf = buf.slice(0, -1); }
		buf = buf.replace(/\r\n?/g, '\n');
		var frames = [], idx;
		while ((idx = buf.indexOf('\n\n')) !== -1) {
			var block = buf.slice(0, idx);
			buf = buf.slice(idx + 2);
			var ev = 'message', data = [], seen = false;
			var lines = block.split('\n');
			for (var i = 0; i < lines.length; i++) {
				var line = lines[i];
				if (!line || line.charAt(0) === ':') continue;
				var c = line.indexOf(':');
				var field = c === -1 ? line : line.slice(0, c);
				var val = c === -1 ? '' : line.slice(c + 1);
				if (val.charAt(0) === ' ') val = val.slice(1);
				if (field === 'event') { ev = val; seen = true; } else if (field === 'data') { data.push(val); seen = true; }
			}
			if (seen) frames.push({ event: ev, data: data.join('\n') });
		}
		return { frames: frames, rest: buf + tail };
	}

	function frameJson(frame) {
		if (!frame || typeof frame.data !== 'string' || frame.data.length > 400000) return null;
		try { var v = JSON.parse(frame.data); return v && typeof v === 'object' ? v : null; } catch (e) { return null; }
	}

	function statusLabel(d) {
		return d && typeof d.label === 'string' ? str(d.label, 80) : '';
	}
	function isOpenNow(date) {
		try {
			var parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', weekday: 'short', hour: 'numeric', hourCycle: 'h23' }).formatToParts(date || new Date());
			var wd = '', hr = -1;
			for (var i = 0; i < parts.length; i++) {
				if (parts[i].type === 'weekday') wd = parts[i].value;
				if (parts[i].type === 'hour') hr = parseInt(parts[i].value, 10);
			}
			if (!wd || hr < 0) return null;
			return /^(Mon|Tue|Wed|Thu|Fri)$/.test(wd) && hr >= 9 && hr < 17;
		} catch (e) { return null; }
	}

	function sumPrices(cards) {
		var t = 0;
		for (var i = 0; i < cards.length; i++) t += cards[i].price || 0;
		return Math.round(t * 100) / 100;
	}

	function shortTitle(t, n) {
		n = n || 48;
		t = str(t, 200);
		if (t.length <= n) return t;
		var cut = t.slice(0, n);
		var sp = cut.lastIndexOf(' ');
		return (sp > 20 ? cut.slice(0, sp) : cut) + '...';
	}

	// Two short lines, no product title.
	function teaserFor(title) {
		return title ? 'Questions about this rig? Ask here.' : 'Not sure what your rig needs? Ask here.';
	}

	// "talk to support" in its usual forms, or "talk to" plus the owner's first name (it is on the store's pages), typed by a visitor, gets the call card on the spot.
	function isSupportRequest(v) {
		return /^\s*(?:talk|speak) (?:to|with) (?:tim|(?:simdrive |sdp )?(?:customer )?support)\W*$/i.test(String(v));
	}

	function viewportName(w) { return w <= 640 ? 'phone' : w <= 1024 ? 'tablet' : 'desktop'; }

	// A markdown link and nothing else (no bracket or newline in the label, no space or bracket in the href).
	var MD_LINK = /\[([^\]\[\n]{1,200})\]\((https?:\/\/[^\s()<>"']{1,500})\)/g;

	// Same host as the page; window.location is the authority, a reply cannot name a host.
	function storeHost(href) {
		var u;
		try { u = new URL(href, window.location.href); } catch (e) { return false; }
		if (u.protocol !== 'https:' && u.protocol !== 'http:') return false;
		var here = window.location.hostname.toLowerCase().replace(/^www\./, '');
		var there = u.hostname.toLowerCase().replace(/^www\./, '');
		return there === here || there === 'simdriveperformance.com';
	}

	// Reply links: product, collection, page, policy, blog paths only; never /cart/* (some change state on GET).
	// Rebuilt relative, so it opens on the page's own host.
	function replyPath(href) {
		var p = storePath(href);
		return p && !/^\/cart/.test(p) ? p : '';
	}

	function fill(el, text) {
		function words(target, value) {
			var pattern = /\*\*([^*\n]{1,200})\*\*/g, start = 0, bold;
			while ((bold = pattern.exec(value))) {
				target.appendChild(document.createTextNode(value.slice(start,bold.index)));
				var strong = document.createElement('strong'); strong.textContent = bold[1]; target.appendChild(strong);
				start = pattern.lastIndex;
			}
			target.appendChild(document.createTextNode(value.slice(start)));
		}
		var last = 0, m;
		text = String(text).replace(BIDI, '');
		MD_LINK.lastIndex = 0;
		while ((m = MD_LINK.exec(text))) {
			var label = m[1], href = replyPath(m[2]);
			if (!href) continue;                     // left as the literal characters
			words(el, text.slice(last, m.index));
			var a = document.createElement('a');
			a.href = href;
			a.textContent = label;                   // never markup, never the href
			a.rel = 'noopener';
			el.appendChild(a);
			last = m.index + m[0].length;
		}
		words(el, text.slice(last));
	}

	/* PURE:END */

	function clock(iso) {
		var d = new Date(iso);
		return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
	}

	var root = document.querySelector('[data-sd-chat]');
	if (!root || !window.fetch || !window.crypto) return;

	var tpl = root.querySelector('template');
	var endpoint = (root.getAttribute('data-endpoint') || '').replace(/\/+$/, '');
	if (!tpl || !('content' in tpl)) return;
	if (!/^https:\/\/[^\/]+(\/|$)|^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(endpoint)) return;

	var MAX_CHARS = 2000;
	var HEALTH_TTL = 300000;
	var KEY = 'sd-chat:' + endpoint + ':';
	var origFetch = window.fetch.bind(window);
	var productHandle = root.getAttribute('data-product-handle') || '';
	var productTitle = root.getAttribute('data-sd-product-title') || '';
	var supportEmail = root.getAttribute('data-support-email') || 'support@simdriveperformance.com';
	var phoneDisplay = root.getAttribute('data-phone') || '(952) 649-5710';
	var phoneTel = telDigits(root.getAttribute('data-tel') || '') || telDigits(phoneDisplay) || '+19526495710';
	var hoursText = 'Monday to Friday, 9 AM to 5 PM Central';
	var TALK = 'Talk to SimDrive Support';
	var reduced =!!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

	function read(k) { try { return JSON.parse(window.sessionStorage.getItem(KEY + k)); } catch (e) { return null; } }
	function write(k, v) { try { window.sessionStorage.setItem(KEY + k, JSON.stringify(v)); return true; } catch (e) { return false; } }
	function forget(k) { try { window.sessionStorage.removeItem(KEY + k); } catch (e) { /* blocked: nothing was stored */ } }

	function rid() {
		var b = new Uint8Array(16), s = '';
		window.crypto.getRandomValues(b);
		for (var i = 0; i < b.length; i++) s += (b[i] < 16 ? '0' : '') + b[i].toString(16);
		return s;
	}

	// With sessionStorage blocked, one page view still keeps one conversation id.
	var memSid = '';
	function sessionId() {
		var id = read('id') || memSid;
		if (!id) { id = rid(); write('id', id); }
		memSid = String(id);
		return memSid;
	}

	function coarse() { return !!(window.matchMedia && window.matchMedia('(pointer:coarse)').matches); }
	function isPhone() { return !!(window.matchMedia && window.matchMedia('(max-width: 640px)').matches); }
	// Phones, landscape phones, tablets: the keyboard shrinks the visual viewport only.
	function kbdDevice() { return isPhone() || (coarse() && (window.innerWidth || 0) <= 1024); }

	// One probe per endpoint per tab per 5 minutes; only a SUCCESS is remembered, so a dead host is retried on the
	// next open. It answers: may we render (configured and the owner's switch), the name, greeting and the strings
	// the owner can change without a theme push.
	function health(cb, fresh) {
		if (!fresh) {
			var hit = read('health');
			if (hit && hit.d && typeof hit.t === 'number' && Date.now() - hit.t < HEALTH_TTL && Date.now() >= hit.t) {
				cb(true, cleanHealth(hit.d), false);
				return;
			}
		}
		var controller = window.AbortController ? new AbortController() : null;
		var done = false;
		function finish(d) {
			if (done) return;
			done = true; clearTimeout(timer);
			var off = !!(d && d.enabled === false);
			var ok = !!(d && d.configured === true && d.ready !== false && !off);
			if (ok) write('health', { t: Date.now(), d: d });
			cb(ok, d ? cleanHealth(d) : null, off);
		}
		var timer = setTimeout(function () { finish(null); if (controller) controller.abort(); }, 5000);
		origFetch(endpoint + '/health', { credentials: 'omit', cache: 'no-store', signal: controller ? controller.signal : undefined })
			.then(function (r) { return r.ok ? r.json() : null; }).then(finish).catch(function () { finish(null); });
	}

	function cleanHealth(d) {
		return {
			name: typeof d.assistant_name === 'string' ? str(d.assistant_name, 60) : '',
			greeting: typeof d.greeting === 'string' ? str(d.greeting, 300) : '',
			label: str(d.launcher_label, 40),
			teaser: str(d.teaser, 160),
			phone: /^[\d\s()+.-]{7,24}$/.test(d.phone || '') ? d.phone.trim() : '',
			tel: typeof d.tel === 'string' ? telDigits(d.tel) : '',
			hours: str(d.hours, 60)
		};
	}

	function mount(info, ok) {
		root.appendChild(document.importNode(tpl.content, true));
		var launcher = root.querySelector('[data-sd-chat-open]');
		var panel = root.querySelector('.sd-chat__panel');
		var closer = root.querySelector('[data-sd-chat-close]');
		var form = root.querySelector('form');
		var input = root.querySelector('textarea');
		var send = root.querySelector('[data-sd-chat-send]');
		var list = root.querySelector('[data-sd-chat-log]');
		if (!launcher || !panel || !form || !input || !send || !list) return;
		var status = root.querySelector('[data-sd-chat-status]');
		var title = root.querySelector('.sd-chat__title');
		var intro = root.querySelector('[data-sd-intro]');
		var introText = root.querySelector('[data-sd-intro-text]');
		var callBtn = root.querySelector('[data-sd-call]');
		var jump = root.querySelector('[data-sd-jump]');
		var teaser = root.querySelector('[data-sd-teaser]');
		var teaserOpen = root.querySelector('[data-sd-teaser-open]');
		var teaserText = root.querySelector('[data-sd-teaser-text]');
		var teaserGo = root.querySelector('[data-sd-teaser-go]');
		var teaserX = root.querySelector('[data-sd-teaser-close]');
		var labelEl = root.querySelector('[data-sd-label]');

		var offline = !ok;
		var inFlight = false;
		var isOpen = false;
		var cardViews = [];
		var addAllViews = [];
		var starters = null;
		var suggestEl = null;
		var offlineEl = null;
		var typing = null;
		var typingLabel = null;
		var slowTimer = 0;
		var teaserTimer = 0;
		var nudgeTopic = null;
		var closeTimer = 0;
		var epoch = 0;            // bumped by Start over: an answer or a cart note that began in an earlier epoch is dropped
		var inflight = null;      // AbortController of the request being answered
		var resetEl = null, resetOpenBtn = null, resetAsk = null, resetNoBtn = null;
		var ctx = { tel: phoneTel, email: supportEmail };
		var serverInfo = info || null;
		// A "show more" button that folds itself away once used.
		function fold(text, fn) {
			var b = h('button', 'sd-chat__more', text);
			b.addEventListener('click', function () { fn(); removeEl(b); });
			return b;
		}
		function h(tag, cls, text) {
			var e = document.createElement(tag);
			if (tag === 'button') e.type = 'button';
			if (cls) e.className = cls;
			if (text != null) e.textContent = text;
			return e;
		}
		var SVG_NS = 'http://www.w3.org/2000/svg';
		var ICONS = {
			phone: 'M6.6 3.5h3l1.4 4-2 1.3a11 11 0 0 0 5.2 5.2l1.3-2 4 1.4v3a2 2 0 0 1-2.1 2A15.5 15.5 0 0 1 4.6 5.6a2 2 0 0 1 2-2.1z',
			mail: 'M3.5 6h17v12h-17zM3.8 6.4 12 13l8.2-6.6',
			check: 'M5 12.5 10 17.5 19.5 7',
			quote: 'M6 3.5h8l4 4v13H6zM14 3.5v4h4M9 12h6M9 15.5h6',
			cart: 'M3 4h2.4l2.2 10.5h9.6l2-7.5H6.6M9.5 19.2h.1M16.5 19.2h.1',
			retry: 'M20 12a8 8 0 1 1-2.6-5.9M20 4v4.5h-4.5',
			warn: 'M12 4 3 20h18zM12 10v4.5M12 17.2h.01'
		};
		function at(el, o) { for (var k in o) el.setAttribute(k, o[k]); }
		function icon(name, size) {
			var s = document.createElementNS(SVG_NS, 'svg'), p = document.createElementNS(SVG_NS, 'path'), ph = name === 'phone';
			size = String(size || 20);
			at(s, { viewBox: '0 0 24 24', width: size, height: size, 'aria-hidden': 'true', focusable: 'false', 'class': 'sd-chat__ico' });
			at(p, { d: ICONS[name], fill: ph ? 'currentColor' : 'none', stroke: ph ? 'none' : 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' });
			s.appendChild(p);
			return s;
		}
		function announce(t) { if (status) status.textContent = t || ''; }
		var cart = { items: [], count: 0, at: 0, dirty: true, loading: null };
		function loadCart(force) {
			if (!force && !cart.dirty && Date.now() - cart.at < 30000) return Promise.resolve(cart);
			if (cart.loading) return cart.loading;
			cart.loading = origFetch('/cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
				.then(function (r) { return r.ok ? r.json() : null; })
				.then(function (d) {
					if (!d) return;
					var p = parseCart(d);
					cart.items = p.items; cart.count = p.count; cart.at = Date.now(); cart.dirty = false;
				})
				.catch(function () { /* the cart is a convenience; the chat works without it */ })
				.then(function () { cart.loading = null; return cart; });
			return cart.loading;
		}
		function inCartCard(c) {
			if (c.inCart) return true;
			if (!c.variantId) return false;
			for (var i = 0; i < cart.items.length; i++) if (cart.items[i].variantId === c.variantId) return true;
			return false;
		}
		var adding = false;       // one add at a time: a second tap while one is in flight would put 2 of a part in the cart
		var boardViews = [];
		function updateCardStates() {
			var inSlots = {};
			for (var i = 0; i < cardViews.length; i++) {
				var v = cardViews[i];
				if (!v.btn.isConnected) continue;
				var inCart = inCartCard(v.card);
				if (inCart && v.card.slot) inSlots[v.card.slot] = 1;
				v.btn.disabled = inCart || adding;
				v.btn.classList.toggle('is-done', inCart);
				v.btn.firstChild.nodeValue = inCart ? 'In cart' : (v.busy ? 'Adding...' : 'Add to cart');
				v.btn.setAttribute('aria-label', (inCart ? 'In your cart: ' : 'Add to cart: ') + v.card.title);
				v.el.classList.toggle('is-in-cart', inCart);
			}
			// The checklist follows the cart: a row whose slot has a card in the cart becomes "In cart".
			for (var k = 0; k < boardViews.length; k++) {
				var bv = boardViews[k];
				if (!bv.li.isConnected || !inSlots[bv.slot] || bv.state === 'have' || bv.state === 'in_cart') continue;
				bv.state = 'in_cart';
				bv.li.className = 'sd-chat__row is-in_cart';
				bv.stateEl.textContent = 'In cart';
				if (bv.el.tagName === 'BUTTON') bv.el.disabled = true;
			}
			for (var j = 0; j < addAllViews.length; j++) {
				var a = addAllViews[j];
				if (!a.el.isConnected) continue;
				var todo = a.cards.filter(function (c) { return !inCartCard(c); });
				a.el.hidden = todo.length < 2;
				a.el.disabled = adding;
				if (todo.length >= 2) {
					a.text.nodeValue = 'Add the ' + todo.length + ' you need';
					a.sum.textContent = formatPrice(sumPrices(todo)) || '';
				}
			}
		}
		var cartTimer = 0;
		function cartChanged(addedItems) {
			cart.dirty = true;
			clearTimeout(cartTimer);
			cartTimer = setTimeout(function () {
				loadCart(true).then(updateCardStates);
			}, 450);
			if (addedItems && addedItems.length) maybeNudge(addedItems);
		}

		// The theme and its cart apps call fetch/XHR for /cart/add|change|update|clear: watch, never interfere.
		var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
		function watchCart() {
			var prev = window.fetch;
			window.fetch = function sdChatFetchWatch(input) {
				var p = prev.apply(this, arguments);
				try {
					var url = typeof input === 'string' ? input : (input && input.url) || '';
					if (CART_WRITE.test(url)) {
						var isAdd = /\/cart\/add/.test(url);
						p.then(function (r) {
							if (!r || !r.ok) return;
							if (isAdd) r.clone().json().then(function (d) { cartChanged(parseAdded(d)); }, function () { cartChanged(null); });
							else cartChanged(null);
						}).catch(function () {});
					}
				} catch (e) { /* never break the page's own request */ }
				return p;
			};
			var X = window.XMLHttpRequest;
			if (X && X.prototype && X.prototype.open) {
				var open = X.prototype.open;
				X.prototype.open = function sdChatXhrWatch(method, url) {
					try {
						if (typeof url === 'string' && CART_WRITE.test(url)) {
							var isAdd = /\/cart\/add/.test(url), xhr = this;
							this.addEventListener('load', function () {
								if (xhr.status < 200 || xhr.status >= 300) return;
								var added = null;
								if (isAdd) { try { added = parseAdded(JSON.parse(xhr.responseText)); } catch (e) { added = null; } }
								cartChanged(added);
							});
						}
					} catch (e) { /* ignore */ }
					return open.apply(this, arguments);
				};
			}
		}

		// ---- teaser and cart nudge: one bubble, never opens the chat ----
		var hideTimer = 0;
		// True when the bubble would sit on the page's Add to cart or payment buttons.
		function coversBuy() {
			var r = teaser.getBoundingClientRect();
			var pts = [[r.left + 3, r.top + 3], [r.right - 3, r.top + 3], [r.left + 3, r.bottom - 3], [r.right - 3, r.bottom - 3], [(r.left + r.right) / 2, (r.top + r.bottom) / 2]];
			for (var i = 0; i < pts.length; i++) {
				var els = document.elementsFromPoint(pts[i][0], pts[i][1]);
				for (var j = 0; j < els.length; j++) if (els[j].closest && els[j].closest('[name=add],.shopify-payment-button')) return true;
			}
			return false;
		}
		function teaserShow(text, go) {
			if (!teaser || isOpen || offline) return false;
			teaserText.textContent = text;
			if (teaserGo) teaserGo.hidden = !go;
			root.style.setProperty('--sdc-lw', launcher.offsetWidth + 'px');
			teaser.hidden = false;
			if (!go && coversBuy()) { teaser.hidden = true; return false; }
			void teaser.offsetWidth;
			teaser.classList.add('is-in');
			clearTimeout(hideTimer);
			hideTimer = setTimeout(teaserHide, go ? 15000 : 8000);
			return true;
		}
		function teaserHide() {
			if (!teaser) return;
			clearTimeout(teaserTimer); clearTimeout(hideTimer);
			teaser.classList.remove('is-in');
			teaser.hidden = true;
			nudgeTopic = null;
		}
		var teaserWaits = false;   // held back because it would have covered a buy button: try again when the page scrolls
		function scheduleTeaser(delay) {
			var state = read('teaser');
			if (state === 'dismissed' || state === 'chatted' || state === 'shown' || !teaser) return;
			if (delay == null) {
				delay = parseInt(root.getAttribute('data-teaser-delay') || '', 10);
				if (!(delay >= 0)) delay = 12000;
			}
			teaserTimer = setTimeout(function () {
				if (isOpen || offline || read('teaser')) return;
				teaserWaits = !teaserShow(serverInfo && serverInfo.teaser ? serverInfo.teaser : teaserFor(productTitle), false);
				if (!teaserWaits) write('teaser', 'shown');
			}, delay);
		}
		function maybeNudge(added) {
			if (isOpen || offline || !teaser) return;
			var done = read('nudged');
			if (!Array.isArray(done)) done = [];
			for (var i = 0; i < added.length; i++) {
				if (!isFoundation(added[i]) || done.indexOf(added[i].handle) !== -1) continue;
				done.push(added[i].handle);
				write('nudged', done.slice(-10));
				clearTimeout(teaserTimer);
				nudgeTopic = added[i];
				teaserShow('Added. Want to see what else it needs?', true);
				return;
			}
		}
		var lastAnswer = null;
		var transcript = read('log');
		if (!Array.isArray(transcript)) transcript = [];

		function removeEl(e) { if (e && e.parentNode) e.parentNode.removeChild(e); }

		function setTop(top, smooth) {
			top = Math.max(0, top);
			if (smooth && !reduced && list.scrollTo) { try { list.scrollTo({ top: top, behavior: 'smooth' }); return; } catch (e) { /* fall through */ } }
			list.scrollTop = top;
		}
		function toEnd(smooth) { setTop(list.scrollHeight, smooth); }
		function toMessage(li) { setTop(li.offsetTop - 10, true); }

		function lastMsg() { var m = list.querySelectorAll('.sd-chat__msg'); return m[m.length - 1]; }
		// The "latest" chip shows only after scrolling UP past the newest message (never over a fresh answer's top).
		var scrollTick = 0;
		function onScroll() {
			if (scrollTick) return;
			scrollTick = setTimeout(function () {
				scrollTick = 0;
				var lm = lastMsg();
				if (jump) jump.hidden = !(lm && list.scrollTop < lm.offsetTop - 80);
			}, 60);
		}

		function msgShell(role, label, at) {
			var li = h('li', 'sd-chat__msg sd-chat__msg--' + role);
			li.appendChild(h('span', 'sd-chat__sr', label + (at && clock(at) ? ' at ' + clock(at) : '') + ': '));
			return li;
		}

		function actionButton(a) {
			var el = h('a', 'sd-chat__act sd-chat__act--' + a.type);
			el.href = a.href;
			if (a.type === 'call') {
				el.appendChild(icon('phone', 22));
				el.appendChild(h('span', 'sd-chat__act-label', a.label));
			} else {
				el.appendChild(icon(a.type === 'email' ? 'mail' : 'quote', 20));
				el.appendChild(h('span', 'sd-chat__act-label', a.label));
			}
			return el;
		}

		function renderActions(actions) {
			var box = h('div', 'sd-chat__acts');
			for (var i = 0; i < actions.length; i++) {
				var a = actions[i];
				var wrap = h('div', 'sd-chat__act-row');
				wrap.appendChild(actionButton(a));
				if (a.type === 'call') {
					var open = a.openNow;
					if (open == null) open = isOpenNow(new Date());
					var line = h('p', 'sd-chat__act-note' + (open === false ? ' is-closed' : ' is-open'));
					line.appendChild(h('i', 'sd-chat__pulse'));
					var hrs = a.hours || hoursText;
					line.appendChild(document.createTextNode(open === false
						? 'After hours (' + hrs + '). Leave a voicemail with your order number.'
						: open === true ? 'Support is open now (' + hrs + ').' : hrs));
					wrap.appendChild(line);
				}
				box.appendChild(wrap);
			}
			return box;
		}

		function renderBoard(items) {
			var board = h('div', 'sd-chat__board');
			board.appendChild(h('p', 'sd-chat__board-title', 'What your rig still needs'));
			var ul = h('ul', 'sd-chat__board-list');
			ul.setAttribute('role', 'list');
			var nOpt = 0, nMiss = 0;
			for (var i = 0; i < items.length; i++) {
				if (items[i].state === 'optional') nOpt++;
				if (items[i].state === 'missing') nMiss++;
				(function (it) {
					var li = h('li', 'sd-chat__row is-' + it.state);
					var asks = it.state === 'missing' || it.state === 'optional';
					var el = asks ? h('button', 'sd-chat__row-in') : h('div', 'sd-chat__row-in');
					if (asks) {
						el.addEventListener('click', function () { submit('Show me ' + it.label.toLowerCase() + ' options'); });
					}
					el.appendChild(h('span', 'sd-chat__tick'));
					var txt = h('span', 'sd-chat__row-text');
					txt.appendChild(h('span', 'sd-chat__row-label', it.label));
					if (it.note) txt.appendChild(h('span', 'sd-chat__row-note', it.note));
					el.appendChild(txt);
					var st = h('span', 'sd-chat__row-state',
						it.state === 'have' ? 'Have' : it.state === 'in_cart' ? 'In cart' : it.state === 'missing' ? 'Needed' : 'Optional');
					el.appendChild(st);
					li.appendChild(el);
					ul.appendChild(li);
					boardViews.push({ slot: it.slot, state: it.state, li: li, el: el, stateEl: st });
				})(items[i]);
			}
			board.appendChild(ul);
			if (nOpt && nMiss) {            // the missing parts first; optional ones one tap away (keeps the board short)
				board.classList.add('is-short');
				board.appendChild(fold('Show ' + nOpt + ' optional part' + (nOpt > 1 ? 's' : ''), function () { board.classList.remove('is-short'); }));
			}
			return board;
		}

		function renderCard(c) {
			var el = h('div', 'sd-chat__card');
			el.setAttribute('data-handle', c.handle);
			if (c.role) el.setAttribute('data-role', c.role);
			var media = h('a', 'sd-chat__card-media');
			media.href = c.href; media.tabIndex = -1; media.setAttribute('aria-hidden', 'true');
			if (c.image) {
				var img = h('img');
				img.src = c.image; img.alt = ''; img.width = 72; img.height = 72;
				img.loading = 'lazy'; img.decoding = 'async';
				img.addEventListener('error', function () { removeEl(img); });
				media.appendChild(img);
			}
			el.appendChild(media);
			var main = h('div', 'sd-chat__card-main');
			if (c.role) main.appendChild(h('span', 'sd-chat__tag', ROLES[c.role]));
			var t = h('a', 'sd-chat__card-title', c.title);
			t.href = c.href;
			main.appendChild(t);
			if (c.brand) main.appendChild(h('p', 'sd-chat__card-brand', c.brand));
			var meta = h('p', 'sd-chat__card-meta');
			var pl = priceLabel(c);
			meta.appendChild(h('strong', 'sd-chat__price', pl || 'Price on the product page'));
			if (c.available === true) meta.appendChild(h('span', 'sd-chat__stock is-in', 'In stock'));
			else if (c.available === false) meta.appendChild(h('span', 'sd-chat__stock is-out', 'Out of stock'));
			main.appendChild(meta);
			if (c.leadTime) main.appendChild(h('p', 'sd-chat__card-lead', /^\d/.test(c.leadTime) ? 'Lead time ' + c.leadTime : c.leadTime));
			var unsure = c.fit === 'unconfirmed';
			var note = c.note || (unsure ? 'Fit not confirmed by the store - ask SimDrive Support before buying' : '');
			if (note) {      // fit is what a nervous buyer needs most: it gets an icon and weight, not the quietest text on the card
				var np = h('p', 'sd-chat__card-note' + (c.fit ? ' is-' + c.fit : ''));
				if (c.fit) np.appendChild(icon(unsure ? 'warn' : 'check', 16));
				np.appendChild(document.createTextNode(note));
				main.appendChild(np);
			}
			el.appendChild(main);
			var acts = h('div', 'sd-chat__card-acts');
			if (c.canAdd) {
				var add = h('button', 'sd-chat__btn ' + (unsure ? 'sd-chat__btn--quiet' : 'sd-chat__btn--add'));
				add.appendChild(document.createTextNode('Add to cart'));
				var view = { card: c, btn: add, el: el, busy: false };
				add.addEventListener('click', function () { addToCart([c], view); });
				cardViews.push(view);
				acts.appendChild(add);
			} else if (c.optionsRequired) {
				var opt = h('a', 'sd-chat__btn sd-chat__btn--add', 'Choose options');
				opt.href = c.href; opt.setAttribute('aria-label', 'Choose options: ' + c.title);
				acts.appendChild(opt);
			}
			var vw = h('a', 'sd-chat__btn sd-chat__btn--quiet', unsure ? 'Ask Support about fit' : 'View');
			vw.href = unsure ? 'tel:' + phoneTel : c.href;
			if (!unsure) vw.setAttribute('aria-label', 'View: ' + c.title);
			acts.appendChild(vw);
			el.appendChild(acts);
			return el;
		}

		function renderCards(cards) {
			var wrap = h('div', 'sd-chat__cards');
			var need = neededCards(cards);
			// Two cards at first (more on request), never hiding one the Add bar counts.
			var cap = 2;
			for (var n = 0; n < cards.length; n++) if (need.indexOf(cards[n]) !== -1) cap = Math.max(cap, n + 1);
			var rest = [];
			for (var i = 0; i < cards.length; i++) {
				var ce = renderCard(cards[i]);
				if (i >= cap) { ce.hidden = true; rest.push(ce); }
				wrap.appendChild(ce);
			}
			if (rest.length) {
				wrap.appendChild(fold('Show ' + rest.length + ' more', function () { rest.forEach(function (e) { e.hidden = false; }); }));
			}
			if (need.length >= 2) {
				var bar = h('button', 'sd-chat__addall');
				var txt = document.createTextNode('');
				var sum = h('span', 'sd-chat__addall-sum', '');
				bar.appendChild(icon('cart', 22));
				var lab = h('span', 'sd-chat__addall-label');
				lab.appendChild(txt);
				bar.appendChild(lab);
				bar.appendChild(sum);
				addAllViews.push({ el: bar, cards: need, text: txt, sum: sum });
				bar.addEventListener('click', function () {
					addToCart(need.filter(function (c) { return !inCartCard(c); }), null);
				});
				wrap.appendChild(bar);
			}
			return wrap;
		}

		function addMessage(role, text, at, persist, extra) {
			extra = extra || {};
			var li = msgShell(role, role === 'user' ? 'You' : 'Assistant', at);
			var p = h('p', 'sd-chat__bubble');
			fill(p, text);
			li.appendChild(p);
			var cards = extra.cards || [], board = extra.checklist || [], acts = extra.actions || [];
			// Order: answer, quick replies (suggestions() inserts them), the short checklist, the cards.
			if (board.length) li.appendChild(renderBoard(board));
			if (cards.length) li.appendChild(renderCards(cards));
			if (acts.length) li.appendChild(renderActions(acts));
			list.appendChild(li);
			if (role !== 'user') lastAnswer = li;
			if (persist) {
				var rec = { r: role, t: text, a: at };
				if (extra.raw) { rec.c = extra.raw.c; rec.l = extra.raw.l; rec.x = extra.raw.x; }   // wire shape, re-validated on restore
				transcript.push(rec);
				if (transcript.length > 60) transcript.splice(0, transcript.length - 60);
				saveTranscript();
			}
			if (role === 'user') syncReset();
			updateCardStates();
			return li;
		}

		// ---- Start over: the first line of the conversation, so a visitor finds it by scrolling up ----
		function buildReset() {
			resetEl = h('li', 'sd-chat__reset');
			resetEl.hidden = true;
			resetEl.setAttribute('aria-live', 'off');   // adding it is not news for the polite log
			resetOpenBtn = h('button', 'sd-chat__reset-btn');
			resetOpenBtn.setAttribute('aria-label', 'Start over: clear this conversation and begin a new chat');
			resetOpenBtn.appendChild(icon('retry', 16));
			resetOpenBtn.appendChild(h('span', null, 'Start over'));
			resetOpenBtn.addEventListener('click', function () {
				resetOpenBtn.hidden = true;
				resetAsk.hidden = false;
				resetNoBtn.focus();   // the safe answer holds the focus
			});
			resetAsk = h('div', 'sd-chat__reset-ask');
			resetAsk.hidden = true;
			resetAsk.setAttribute('role', 'group');
			resetAsk.setAttribute('aria-labelledby', 'SdChatResetQ');
			var q = h('p', 'sd-chat__reset-q', 'Start a new chat? This clears the conversation.');
			q.id = 'SdChatResetQ';
			var acts = h('div', 'sd-chat__reset-acts');
			var yes = h('button', 'sd-chat__btn sd-chat__btn--dark', 'Start over');
			yes.addEventListener('click', resetChat);
			resetNoBtn = h('button', 'sd-chat__btn sd-chat__btn--quiet', 'Keep chatting');
			resetNoBtn.addEventListener('click', function () { hideAsk(true); });
			acts.appendChild(yes); acts.appendChild(resetNoBtn);
			resetAsk.appendChild(q); resetAsk.appendChild(acts);
			resetEl.appendChild(resetOpenBtn); resetEl.appendChild(resetAsk);
			list.insertBefore(resetEl, list.firstChild);
		}
		function hideAsk(refocus) {
			if (!resetAsk || resetAsk.hidden) return;
			resetAsk.hidden = true;
			resetOpenBtn.hidden = false;
			if (refocus) resetOpenBtn.focus({ preventScroll: true });
		}
		// Offered once the visitor has said something; before that there is nothing to clear.
		function syncReset() {
			if (!resetEl) return;
			var has = !!list.querySelector('.sd-chat__msg--user');
			resetEl.hidden = !has;
			if (!has) hideAsk(false);
		}
		// Back to the greeting and the starter chips with a new conversation id. The cart is never touched. The old
		// conversation stays on the server for the owner to read; nothing is sent.
		function resetChat() {
			epoch++;
			if (inflight) { try { inflight.abort(); } catch (e) { /* already finished */ } inflight = null; }
			busy(false);
			for (var i = list.children.length - 1; i >= 0; i--) {
				var k = list.children[i];
				if (k !== intro && k !== resetEl) list.removeChild(k);
			}
			cardViews.length = 0; addAllViews.length = 0; boardViews.length = 0;
			lastAnswer = null; suggestEl = null; offlineEl = null;
			transcript = [];
			forget('log'); forget('pending'); forget('id');
			memSid = '';
			auto409 = 0;
			input.value = ''; grow();
			if (jump) jump.hidden = true;
			hideAsk(false);
			syncReset();
			hideStarters();
			if (offline) showOffline(); else showStarters();
			list.scrollTop = 0;
			announce('Started a new chat.');
			if (!coarse() && !offline) input.focus({ preventScroll: true }); else list.focus({ preventScroll: true });
		}

		function saveTranscript() {
			if (write('log', transcript)) return;
			transcript.splice(0, Math.ceil(transcript.length / 2));   // quota: keep the newer half
			write('log', transcript);
		}

		function addNote(cls, text) {
			var li = h('li', 'sd-chat__note ' + cls);
			li.appendChild(h('p', 'sd-chat__note-text', text));
			list.appendChild(li);
			return li;
		}

		// `reuse`: the request may have reached the server (lost connection, still being answered), so the same id is
		// sent again. After an error the server REPORTED, it replays the stored failure for that id: a fresh id is needed.
		function addError(code, detail, retry, reuse) {
			var li = msgShell('error', 'Problem');
			var p = h('p', 'sd-chat__bubble');
			p.appendChild(document.createTextNode(
				typeof detail === 'string' && detail ? detail.slice(0, 600)
					: code === 429 ? 'Too many messages at once. Please try again in a minute.'
					: code === 503 ? 'The assistant is busy or offline at the moment.'
					: 'That message did not get an answer.'));
			li.appendChild(p);
			var row = h('div', 'sd-chat__err-row');
			if (retry) {
				var rb = h('button', 'sd-chat__btn sd-chat__btn--retry');
				rb.appendChild(icon('retry', 18));
				rb.appendChild(document.createTextNode('Try again'));
				rb.addEventListener('click', function () {
					removeEl(li);
					submit(retry.message, reuse ? retry : Object.assign({}, retry, { request_id: rid() }));
				});
				row.appendChild(rb);
			}
			var call = normalizeAction({ type: 'call', label: callLabel() }, ctx);
			if (call) row.appendChild(actionButton(call));
			li.appendChild(row);
			list.appendChild(li);
			toMessage(li);
			return li;
		}
		function callLabel() { return 'Call SimDrive Support: ' + phoneDisplay; }
		function startersList() {
			return productHandle
				? ["What's included?", 'What else do I need?', 'Will it fit my setup?', TALK]
				: ['Help me choose a rig', 'Compare motion platforms', 'Shipping and delivery', TALK];
		}
		function showStarters() {
			if (starters || !intro) return;
			starters = h('ul', 'sd-chat__starters');
			starters.setAttribute('role', 'list');
			startersList().forEach(function (text) {
				var li = h('li', 'sd-chat__starter-item');
				var b = h('button', 'sd-chat__starter');
				if (text === TALK) b.appendChild(icon('phone', 20));
				b.appendChild(h('span', null, text));
				b.addEventListener('click', function () { if (offline) return; if (text === TALK) talkToSupport(); else submit(text); });
				li.appendChild(b);
				starters.appendChild(li);
			});
			intro.appendChild(starters);
		}
		function hideStarters() { removeEl(starters); starters = null; }

		function suggestions(items, host) {
			removeEl(suggestEl); suggestEl = null;
			var texts = normalizeSuggestions(items);
			var bubble = host && host.querySelector('.sd-chat__bubble');
			if (!texts.length || !bubble) return;
			suggestEl = h('div', 'sd-chat__suggest');
			texts.forEach(function (text) {
				var chip = h('button', 'sd-chat__chip', text);
				chip.addEventListener('click', function () { if (!offline) submit(text); });
				suggestEl.appendChild(chip);
			});
			host.insertBefore(suggestEl, bubble.nextSibling);   // right under the question it answers, above the cards
		}

		var OFFLINE_TEXT = 'The assistant is offline right now. A person will answer at ' + supportEmail + ' or (952) 649-5710. '
			+ 'For a rig over $2,000, use Request a written quote on the product page so our support team can review the equipment, freight and next steps with you.';

		function showOffline() {
			if (offlineEl) return;
			var li = msgShell('assistant', 'Assistant');
			li.tabIndex = -1;
			var p = h('p', 'sd-chat__bubble');
			fill(p, OFFLINE_TEXT);
			li.appendChild(p);
			li.appendChild(renderActions(normalizeActions([
				{ type: 'call', label: callLabel(), tel: phoneTel },
				{ type: 'email', href: 'mailto:' + supportEmail },
				{ type: 'quote', url: '/pages/request-a-quote' }
			], ctx)));
			var retry = h('button', 'sd-chat__btn sd-chat__btn--retry');
			retry.appendChild(icon('retry', 18));
			retry.appendChild(document.createTextNode('Check the connection'));
			retry.addEventListener('click', function () { reprobe(true); });
			li.appendChild(retry);
			offlineEl = li;
			list.appendChild(li);
		}
		function hideOffline() { removeEl(offlineEl); offlineEl = null; }

		var placeholder0 = input.getAttribute('placeholder') || '';
		function setOffline(on) {
			var hadFocus = document.activeElement === input;
			offline = on;
			input.disabled = on;
			input.placeholder = on ? 'Assistant unavailable' : placeholder0;
			send.disabled = on || inFlight;
			panel.classList.toggle('is-offline', on);
			if (on) {
				hideStarters(); showOffline(); teaserHide();
				if (hadFocus && offlineEl) offlineEl.focus({ preventScroll: true });   // focus must not fall to the top of the page
			} else { hideOffline(); if (!transcript.length && !list.querySelector('.sd-chat__msg')) showStarters(); }
		}

		function applyInfo(i) {
			if (!i) return;
			serverInfo = i;
			if (i.name && title) title.textContent = i.name;
			if (i.greeting && introText) introText.textContent = i.greeting;
			if (i.label && labelEl) labelEl.textContent = i.label;
			if (i.phone || i.tel) {
				var t = i.tel || telDigits(i.phone);
				if (t) {
					phoneTel = t; phoneDisplay = i.phone || phoneDisplay; ctx.tel = t;
					if (callBtn) { callBtn.href = 'tel:' + t; callBtn.setAttribute('aria-label', 'Call us, ' + phoneDisplay); }
				}
			}
			if (i.hours) hoursText = i.hours;
		}

		function reprobe(fresh) {
			health(function (ok2, info2, off) {
				if (off) {
					if (isOpen) close(true);
					launcher.hidden = true; teaserHide();
					return;
				}
				launcher.hidden = isOpen;
				if (ok2 && info2) applyInfo(info2);
				setOffline(!ok2);
			}, fresh || offline);
		}
		function busy(on) {
			inFlight = on;
			send.disabled = on || offline;
			list.setAttribute('aria-busy', on ? 'true' : 'false');
			clearTimeout(slowTimer);
			if (on && !typing) {
				typing = h('li', 'sd-chat__msg sd-chat__msg--assistant sd-chat__typing');
				var dots = h('span', 'sd-chat__dots');
				dots.setAttribute('aria-hidden', 'true');
				for (var k = 0; k < 3; k++) dots.appendChild(document.createElement('i'));
				typing.appendChild(dots);
				typingLabel = h('span', 'sd-chat__typing-label', '');
				typing.appendChild(typingLabel);
				list.appendChild(typing);
				announce('The assistant is working on it');
				slowTimer = setTimeout(function () { if (typingLabel && !typingLabel.textContent) typingLabel.textContent = 'Still working on it'; }, 9000);
				toEnd(false);
			} else if (!on && typing) {
				removeEl(typing); typing = null; typingLabel = null;
				announce('');
			}
		}
		function setStatus(label) {
			if (typingLabel && label) { typingLabel.textContent = label; announce(label); }
		}

		function readStream(r) {
			return new Promise(function (resolve, reject) {
				var reader = r.body.getReader();
				var dec = new TextDecoder();
				var buf = '', result = null;
				function handle(frames) {
					for (var i = 0; i < frames.length && !result; i++) {
						var f = frames[i], d = frameJson(f);
						if (f.event === 'status') setStatus(statusLabel(d));
						else if (f.event === 'final') result = { ok: true, code: 200, data: d };
						else if (f.event === 'error') result = { ok: false, code: d && typeof d.status === 'number' ? d.status : 502, data: d };
					}
				}
				function pump() {
					reader.read().then(function (chunk) {
						if (chunk.done) {
							buf += dec.decode();
							handle(parseSSE(buf + '\n\n').frames);
							if (result) resolve(result); else reject(new Error('stream ended'));
							return;
						}
						buf += dec.decode(chunk.value, { stream: true });
						var parsed = parseSSE(buf);
						buf = parsed.rest;
						handle(parsed.frames);
						if (result) { try { reader.cancel(); } catch (e) { /* done anyway */ } resolve(result); return; }
						pump();
					}, reject);
				}
				pump();
			});
		}

		function post(payload) {
			var ctrl = window.AbortController ? new AbortController() : null;
			inflight = ctrl;
			var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 70000) : 0;
			var canStream = !!(window.ReadableStream && window.TextDecoder);
			var headers = { 'Content-Type': 'application/json' };
			if (canStream) headers.Accept = 'text/event-stream';
			return origFetch(endpoint + '/message', {
				method: 'POST', headers: headers, credentials: 'omit',
				signal: ctrl ? ctrl.signal : undefined, body: JSON.stringify(payload)
			}).then(function (r) {
				var ct = r.headers && r.headers.get ? (r.headers.get('content-type') || '') : '';
				if (r.ok && canStream && /text\/event-stream/i.test(ct) && r.body && r.body.getReader) return readStream(r);
				return r.json().then(function (d) { return { ok: r.ok, code: r.status, data: d }; },
					function () { return { ok: r.ok, code: r.status, data: null }; });
			}).then(function (res) { clearTimeout(timer); if (inflight === ctrl) inflight = null; return res; },
				function (err) { clearTimeout(timer); if (inflight === ctrl) inflight = null; throw err; });
		}

		var LOST = 'The connection was interrupted. Trying again will not send your message twice.';
		var auto409 = 0;
		function submit(text, retry) {
			text = String(text || '').replace(/\s+$/, '').slice(0, MAX_CHARS);
			if (!text || offline) return;
			if (!retry && inFlight) return;
			if (retry && inFlight) return;
			if (!retry) {
				addMessage('user', text, new Date().toISOString(), true);
				hideStarters();
				write('teaser', 'chatted'); teaserHide();
				auto409 = 0;
				hideAsk(false);
			}
			input.value = ''; grow();
			removeEl(suggestEl); suggestEl = null;
			busy(true);
			toEnd(true);
			var ep = epoch;
			var cartWait = Promise.race([loadCart(false), new Promise(function (res) { setTimeout(res, 1500); })]);
			cartWait.then(function () {
				if (ep !== epoch) return;   // the visitor started over while the cart was being read
				var payload = retry;
				if (!payload) {
					payload = { session_id: sessionId(), request_id: rid(), message: text,
						page_url: window.location.origin + window.location.pathname, product_handle: productHandle,
						client: { viewport: viewportName(window.innerWidth || 1024), v: '2' } };
					var cp = cartPayload(cart);
					if (cp) payload.cart = cp;
				}
				write('pending', payload);
				return post(payload).then(function (res) { return finish(res, payload, text, ep); },
					function () { if (ep !== epoch) return; busy(false); addError(0, LOST, payload, true); });
			}).then(function () {
				var ae = document.activeElement;   // only take focus back when the visitor has not moved it elsewhere (say, to Call)
				if (isOpen && !coarse() && !offline && !inFlight && (!ae || ae === document.body || ae === send || ae === panel)) input.focus({ preventScroll: true });
			});
		}

		function finish(res, payload, text, ep) {
			if (ep !== epoch) return;   // an answer to a conversation the visitor has since cleared
			var d = res.data, reply = d && typeof d.reply === 'string' ? d.reply.replace(/\s+$/, '') : '';
			if (res.ok && reply) {
				busy(false);
				if (/^[0-9a-f]{32}$/.test(d.session_id || '')) { memSid = d.session_id; write('id', d.session_id); }
				write('pending', null);
				var cap = function (v) { return Array.isArray(v) ? v.slice(0, 8) : []; };
				var extra = {
					cards: normalizeCards(d.cards),
					checklist: normalizeChecklist(d.checklist),
					actions: normalizeActions(d.actions, ctx),
					raw: { c: cap(d.cards), l: cap(d.checklist), x: cap(d.actions) }
				};
				var li = addMessage('assistant', reply, new Date().toISOString(), true, extra);
				var sugg = normalizeSuggestions(d.suggestions);
				suggestions(sugg, li);
				if (sugg.length && transcript.length) { transcript[transcript.length - 1].s = sugg; saveTranscript(); }
				toMessage(li);
				return;
			}
			if (res.ok) {   // the answer frame was cut off: the server may hold the answer, so ask again with the same id
				busy(false);
				addError(0, LOST, payload, true);
				return;
			}
			var stuck = res.code === 409 && /still being answered/i.test(d && d.detail || '');
			if (stuck && auto409 < 2) {
				auto409++;
				return new Promise(function (resolve) {
					setTimeout(function () { if (ep !== epoch) { resolve(); return; } busy(false); resolve(); inFlightRetry(payload); }, 3000);
				});
			}
			write('pending', null);
			busy(false);
			var again = stuck || res.code >= 500 || res.code === 429;
			addError(res.code, d && d.detail, again ? payload : null, stuck);
			if (!again && text) { input.value = text; grow(); }   // keep the words so they can be edited and resent
		}
		function inFlightRetry(payload) { submit(payload.message, payload); }

		// ---- add to cart ----
		// The theme's Slide Cart drawer refetches only when asked.
		function pokeTheme() {
			try {
				if (typeof window.SLIDECART_UPDATE === 'function') window.SLIDECART_UPDATE();
				document.dispatchEvent(new CustomEvent('cart:updated'));
			} catch (e) { /* a theme without the app is unaffected */ }
		}
		function addToCart(cards, view) {
			var items = cards.filter(function (c) { return c.variantId; }).map(function (c) { return { id: c.variantId, quantity: 1 }; });
			if (!items.length || adding) return;
			adding = true;
			var ep = epoch;      // a Start over mid-add: the cart still gets the item, the cleared log does not get a note about it
			if (view) view.busy = true;
			updateCardStates();
			function settle() { adding = false; if (view) view.busy = false; updateCardStates(); }
			// The page's CURRENT fetch, so Shopify's analytics events and upsell apps see the add.
			window.fetch('/cart/add.js', {
				method: 'POST', credentials: 'same-origin',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({ items: items })
			}).then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }, function () { return { ok: r.ok, d: null }; }); })
				.then(function (res) {
					if (!res.ok) { var err = new Error((res.d && (res.d.description || res.d.message)) || 'It could not be added.'); err.api = true; throw err; }
					cart.dirty = true;
					pokeTheme();
					return loadCart(true).then(function () { settle(); addAdded(cards, ep !== epoch); });
				})
				.catch(function (e) {
					settle();
					var msg = e && e.api ? String(e.message).slice(0, 200) : 'The cart did not answer.';
					if (ep === epoch) addNote('sd-chat__note--error', 'That did not go into your cart. ' + msg + (/[.!?]$/.test(msg) ? ' ' : '. ') + 'You can also add it from the product page.');
					showToast('That did not go into your cart.', false);
				});
		}
		// The confirmation is pinned above the composer; the used card flips to "In cart" in place; the log keeps a note.
		var toast = h('div', 'sd-chat__toast'), toastText = h('span', 'sd-chat__toast-text'), toastTimer = 0;
		toast.hidden = true;
		var tv = h('a', 'sd-chat__toast-link', 'View cart'); tv.href = '/cart';
		var tc = h('a', 'sd-chat__toast-link', 'Checkout'); tc.href = '/checkout';
		toast.appendChild(icon('check', 20)); toast.appendChild(toastText); toast.appendChild(tv); toast.appendChild(tc);
		form.parentNode.insertBefore(toast, form);
		function showToast(text, ok) {
			toastText.textContent = text;
			tv.hidden = tc.hidden = !ok;
			toast.classList.toggle('is-bad', !ok);
			toast.hidden = false;
			clearTimeout(toastTimer);
			toastTimer = setTimeout(function () { toast.hidden = true; }, 7000);
		}
		function addAdded(cards, quiet) {
			var text = (cards.length > 1 ? cards.length + ' items' : shortTitle(cards[0].title, 28)) + ' added to your cart.';
			if (!quiet) {
				var li = h('li', 'sd-chat__note sd-chat__note--ok');
				var row = h('p', 'sd-chat__note-text');
				row.appendChild(icon('check', 20));
				row.appendChild(document.createTextNode(text));
				li.appendChild(row);
				list.appendChild(li);
			}
			showToast(text, true);
		}
		function talkToSupport() {
			addMessage('user', TALK, new Date().toISOString(), true);
			hideStarters();
			var open = isOpenNow(new Date());
			var text = open === false
				? 'Support answers Monday to Friday, 9 AM to 5 PM Central. Call anyway and leave a voicemail with your order number, or email ' + supportEmail + '.'
				: 'Call SimDrive Support at ' + phoneDisplay + '. Support answers Monday to Friday, 9 AM to 5 PM Central. Have your order number or the rig you are building handy.';
			var raw = [
				{ type: 'call', label: callLabel(), tel: phoneTel, open_now: open, hours: hoursText },
				{ type: 'email', href: 'mailto:' + supportEmail }
			];
			var li = addMessage('assistant', text, new Date().toISOString(), true, { actions: normalizeActions(raw, ctx), raw: { x: raw } });
			toMessage(li);
		}
		function lock(on) {
			var el = document.documentElement;
			if (el && el.classList) el.classList.toggle('sd-chat-lock', !!on);
		}
		function syncViewport() {
			var vv = window.visualViewport;
			if (!isOpen || !kbdDevice() || !vv) {
				panel.style.removeProperty('--sdc-vh'); panel.style.removeProperty('--sdc-vt');
				panel.classList.remove('is-kbd');
				return;
			}
			panel.style.setProperty('--sdc-vh', Math.round(vv.height) + 'px');
			panel.style.setProperty('--sdc-vt', Math.round(vv.offsetTop) + 'px');
			panel.classList.toggle('is-kbd', vv.height < (window.innerHeight || vv.height) - 120);
		}

		function open(opts) {
			opts = opts || {};
			if (isOpen) return;
			clearTimeout(closeTimer);
			isOpen = true;
			teaserHide();
			panel.hidden = false;
			launcher.hidden = true;
			launcher.setAttribute('aria-expanded', 'true');
			var phone = isPhone();
			panel.setAttribute('aria-modal', phone ? 'true' : 'false');
			if (phone) lock(true);
			syncViewport();
			if (!offline && !transcript.length && !list.querySelector('.sd-chat__msg')) showStarters();
			void panel.offsetWidth;
			panel.classList.add('is-open');
			reprobe(false);
			if (list.querySelector('.sd-chat__msg')) toEnd(false); else list.scrollTop = 0;
			write('open', 1);
			if (!opts.restore) {
				if (!coarse() && !offline) input.focus({ preventScroll: true });
				else panel.focus({ preventScroll: true });
			}
		}

		function close(silent) {
			if (!isOpen) return;
			isOpen = false;
			hideAsk(false);
			panel.classList.remove('is-open');
			lock(false);
			syncViewport();
			launcher.hidden = false;
			launcher.setAttribute('aria-expanded', 'false');
			write('open', 0);
			closeTimer = setTimeout(function () { if (!isOpen) panel.hidden = true; }, reduced ? 0 : 240);
			if (!silent) launcher.focus({ preventScroll: true });
		}

		function onKey(e) {
			if (!isOpen) return;
			if (e.key === 'Escape') {
				e.preventDefault();
				if (resetAsk && !resetAsk.hidden) hideAsk(true); else close();   // Esc answers the Start over question before it closes the chat
				return;
			}
			if (e.key === 'Tab' && isPhone()) {
				var f = panel.querySelectorAll('a[href],button:not([disabled]),textarea:not([disabled])');
				var vis = [];
				for (var i = 0; i < f.length; i++) if (f[i].offsetParent !== null || f[i] === document.activeElement) vis.push(f[i]);
				if (!vis.length) return;
				var first = vis[0], last = vis[vis.length - 1];
				if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last.focus(); }
				else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
			}
		}

		function grow() {
			input.style.height = 'auto';
			input.style.height = Math.min(input.scrollHeight, 140) + 'px';
		}
		if (info) applyInfo(info);
		if (callBtn) callBtn.href = 'tel:' + phoneTel;
		buildReset();
		for (var i = 0; i < transcript.length; i++) {
			var t = transcript[i];
			if (!t || typeof t.t !== 'string') continue;
			var extra = t.r === 'user' ? null : { cards: normalizeCards(t.c), checklist: normalizeChecklist(t.l), actions: normalizeActions(t.x, ctx) };
			addMessage(t.r === 'user' ? 'user' : 'assistant', t.t, t.a, false, extra || undefined);
		}
		if (transcript.length) {
			hideStarters();
			var lastRec = transcript[transcript.length - 1];
			if (lastRec && lastRec.r !== 'user' && lastRec.s) suggestions(lastRec.s, lastAnswer);
		}
		var pending = read('pending');
		if (pending && pending.message && ok) addError(0, 'A previous message lost its connection. You can send it again here.', pending, true);
		setOffline(!ok);   // after the restore, so the offline notice is the LAST thing in the log, where the panel opens

		launcher.addEventListener('click', function () { open(); });
		if (closer) closer.addEventListener('click', function () { close(); });
		panel.addEventListener('keydown', onKey);
		if (teaserOpen) teaserOpen.addEventListener('click', function () {
			var topic = nudgeTopic;
			teaserHide();
			open();
			if (topic) submit('What else does my rig need?');
		});
		if (teaserX) teaserX.addEventListener('click', function () {
			write('teaser', 'dismissed'); teaserHide(); launcher.focus({ preventScroll: true });
		});
		if (jump) jump.addEventListener('click', function () { var lm = lastMsg(); if (lm) toMessage(lm); jump.hidden = true; });
		list.addEventListener('scroll', onScroll, { passive: true });
		form.addEventListener('submit', function (e) {
			e.preventDefault();
			var v = input.value;
			if (isSupportRequest(v)) { input.value = ''; grow(); talkToSupport(); return; }
			submit(v);
		});
		input.addEventListener('input', grow);
		input.addEventListener('focus', function () { if (isPhone()) setTimeout(function () { toEnd(false); }, 250); });
		input.addEventListener('keydown', function (e) {
			if (e.key === 'Enter' && !e.shiftKey && !e.isComposing && !(coarse() && isPhone())) { e.preventDefault(); form.dispatchEvent(new Event('submit', { cancelable: true })); }
		});
		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', syncViewport);
			window.visualViewport.addEventListener('scroll', syncViewport);
		}
		window.addEventListener('resize', function () {
			if (isOpen) { panel.setAttribute('aria-modal', isPhone() ? 'true' : 'false'); lock(isPhone()); syncViewport(); }
		});
		watchCart();

		// On a phone the launcher shrinks to the icon and "Ask" after 8 s or a 200 px scroll.
		var compacted = false;
		function compact() {
			if (compacted || isOpen || !isPhone()) return;
			compacted = true;
			launcher.setAttribute('aria-label', (labelEl && labelEl.textContent) || 'Ask a question');
			launcher.classList.add('is-compact');
		}
		window.addEventListener('scroll', function () {
			if ((window.pageYOffset || 0) > 200) compact();
			if (teaserWaits) { clearTimeout(teaserTimer); scheduleTeaser(700); }
		}, { passive: true });
		setTimeout(compact, 8000);

		window.SdChat = { open: function () { open(); }, close: function () { close(); }, version: '2' };

		// Never the first thing on screen: wait for idle, then (once) invite.
		if (!offline) {
			scheduleTeaser();
			if (!isPhone() && read('open') === 1) open({ restore: true });
		}
	}

	function boot() {
		health(function (ok, info, off) { if (!off) mount(info, ok); });
	}

	// Safari has no requestIdleCallback: waiting for window load there would hold the launcher back on a heavy page over a
	// phone connection, so it starts shortly after the document is parsed instead.
	if (typeof window.requestIdleCallback === 'function') {
		window.requestIdleCallback(boot, { timeout: 2500 });
	} else if (document.readyState === 'loading' && window.addEventListener) {
		document.addEventListener('DOMContentLoaded', function () { setTimeout(boot, 600); });
	} else if (document.readyState === 'interactive') {
		setTimeout(boot, 600);
	} else {
		boot();
	}
})();
